import { PortableText } from '@portabletext/react';
import { Children, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import type { PageBuilderSimpleBlockContent } from '@fhs/cms-groq-queries';
import { type HeadingTextProps, type ParagraphTextProps, Text } from '@fhs/ui';

import { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';

import { ActionLink } from './action-link';

type SimpleBlockContentProps = {
  value: PageBuilderSimpleBlockContent;
  onAction: ActionHandler;
  linkText?: Partial<ParagraphTextProps>;
} & (
  | { normalTextType?: 'paragraph'; normalText?: Partial<ParagraphTextProps> }
  | { normalTextType?: 'heading'; normalText?: Partial<HeadingTextProps> }
);

export function SimpleBlockContent({
  normalTextType = 'paragraph',
  ...props
}: SimpleBlockContentProps) {
  return (
    <PortableText
      value={props.value}
      components={useMemo(
        () => ({
          block: {
            normal: ({ children }) => {
              if (normalTextType === 'paragraph') {
                return (
                  <View style={styles.normalTextWrapper}>
                    <Text.Paragraph size="md" {...props.normalText}>
                      {children}
                    </Text.Paragraph>
                  </View>
                );
              }

              return (
                <View style={styles.normalTextWrapper}>
                  <Text.Heading type="two" {...props.normalText}>
                    {children}
                  </Text.Heading>
                </View>
              );
            },
          },
          marks: {
            link: ({ children, value }) => {
              return (
                <ActionLink
                  action={value}
                  onAction={addActionSource(
                    {
                      text: Children.toArray(children)
                        .filter(child => typeof child === 'string')
                        .join(', '),
                    },
                    props.onAction
                  )}
                >
                  <Text.Paragraph size="md" {...props.linkText}>
                    {children}
                  </Text.Paragraph>
                </ActionLink>
              );
            },
          },
        }),
        [normalTextType, props.normalText, props.onAction, props.linkText]
      )}
    />
  );
}

const styles = StyleSheet.create({
  normalTextWrapper: {
    maxWidth: '100%',
  },
});
